import { useAuthStore } from '~/store/auth';

/**
 * S-FIX-1-FE.B.1 — `useUserScope()` hook.
 *
 * Читает расширенный `/auth/me` response (BE.B.1). Возвращает:
 *   - warehouse_codes: коды складов в scope user-а (пустой массив если unrestricted)
 *   - unrestricted: true для admin/manager
 *   - abilities: мапа entity → actions[]
 *
 * Используется UI guards в wizards (stock-entries, work-orders, stock-reconciliation)
 * для disabled-state кнопок без 403-roundtrip.
 */

export type AuthAction = 'read' | 'create' | 'update' | 'delete' | 'approve';

export interface UserScope {
  warehouse_codes: string[];
  unrestricted: boolean;
  abilities: Record<string, AuthAction[]>;
  /**
   * access-control (B5/F2) — эффективный набор модулей активной компании.
   * `undefined` = BE не прислал (старый билд) → module-gating отключён (back-compat).
   */
  module_access: string[] | undefined;
}

const DEFAULT_SCOPE: UserScope = {
  warehouse_codes: [],
  unrestricted: false,
  abilities: {},
  module_access: undefined,
};

export function useUserScope(): UserScope {
  const user = useAuthStore((s) => s.user);
  if (!user?.scope) return DEFAULT_SCOPE;
  const scope = user.scope as UserScope & { module_access?: string[] };
  return {
    warehouse_codes: user.scope.warehouse_codes ?? [],
    unrestricted: user.scope.unrestricted ?? false,
    abilities: (user.scope.abilities ?? {}) as Record<string, AuthAction[]>,
    module_access: Array.isArray(scope.module_access) ? scope.module_access : undefined,
  };
}

export function isWarehouseInScope(scope: UserScope, code: string): boolean {
  if (scope.unrestricted) return true;
  return scope.warehouse_codes.includes(code);
}

/**
 * F6 (project-actions-parity §5) — единственный источник клиентских прав.
 *
 * До него на фронте жили ТРИ источника, и они расходились: этот `canDo` (читает
 * `scope.abilities` из `/auth/me` — канон), `useAbility` (руками набранная таблица
 * ролей в файле, ключуется по `user.role`) и `DisabledWithReason ability=` (канон,
 * но по ключам, которых в вокабуляре нет). Таблица `useAbility` расходилась с
 * каноном в обе стороны: `company_admin` и `holding_admin` в ней отсутствовали
 * вовсе — `matrix[role] ?? READ_ONLY` — и руководитель компании видел на карточке
 * проекта меню наблюдателя; одновременно `reviewer`, `sme`, кладовщик и планировщик
 * получали `project:write`, которого у них в каноне нет, и упирались в 403.
 *
 * Файл `useAbility.ts` удалён целиком; grep-барьер на его возвращение —
 * `tests/unit/no-useability.test.ts`.
 */
export function canDo(scope: UserScope, entity: string, action: AuthAction): boolean {
  return (scope.abilities[entity] ?? []).includes(action);
}
